import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import {
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  Plus,
  AlertTriangle,
  Calendar
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { es } from 'date-fns/locale';
import type { EmployeeDocument, EmployeeDocumentType, EmployeeDocumentStatus } from '@/types/legacy';
import { EmployeeDocumentForm } from './EmployeeDocumentForm';

const documentTypeLabels: Record<EmployeeDocumentType, string> = {
  sick_leave: 'Baja Médica',
  permission: 'Permiso',
  justification: 'Justificación',
  vacation_request: 'Solicitud Vacaciones',
  complaint: 'Queja / Sugerencia',
  other: 'Otro',
};

const documentTypeIcons: Record<EmployeeDocumentType, string> = {
  sick_leave: '🏥',
  permission: '📋',
  justification: '📄',
  vacation_request: '🏖️',
  complaint: '💬',
  other: '📎',
};

const statusConfig: Record<EmployeeDocumentStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: React.ReactNode }> = {
  pending: { label: 'Pendiente', variant: 'secondary', icon: <Clock className="w-3 h-3" /> },
  reviewed: { label: 'Revisado', variant: 'outline', icon: <Eye className="w-3 h-3" /> },
  approved: { label: 'Aprobado', variant: 'default', icon: <CheckCircle className="w-3 h-3" /> },
  rejected: { label: 'Rechazado', variant: 'destructive', icon: <XCircle className="w-3 h-3" /> },
};

export function EmployeeDocumentsList() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [documents, setDocuments] = useState<EmployeeDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);

  const fetchDocuments = async () => {
    if (!user) return;

    try {
      const { data, error } = await api
        .from('employee_documents')
        .select('*')
        .eq('employee_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDocuments(data || []);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los documentos',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Mis Documentos Enviados</h3>
          <p className="text-sm text-muted-foreground">
            Bajas, permisos, justificaciones y solicitudes
          </p>
        </div>
        <Button onClick={() => setFormOpen(true)} className="bg-gradient-magma">
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Documento
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-3 text-center">
          <p className="text-2xl font-bold">{documents.length}</p>
          <p className="text-xs text-muted-foreground">Total</p>
        </Card>
        <Card className="p-3 text-center">
          <p className="text-2xl font-bold text-yellow-500">
            {documents.filter(d => d.status === 'pending').length}
          </p>
          <p className="text-xs text-muted-foreground">Pendientes</p>
        </Card>
        <Card className="p-3 text-center">
          <p className="text-2xl font-bold text-green-500">
            {documents.filter(d => d.status === 'approved').length}
          </p>
          <p className="text-xs text-muted-foreground">Aprobados</p>
        </Card>
        <Card className="p-3 text-center">
          <p className="text-2xl font-bold text-red-500">
            {documents.filter(d => d.status === 'rejected').length}
          </p>
          <p className="text-xs text-muted-foreground">Rechazados</p>
        </Card>
      </div>

      {/* Lista de documentos */}
      {documents.length === 0 ? (
        <Card className="p-8 text-center">
          <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">No has enviado ningún documento</p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => setFormOpen(true)}
          >
            Enviar mi primer documento
          </Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {documents.map((doc) => {
            const status = statusConfig[doc.status];

            return (
              <Card key={doc.id} className="p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start gap-4">
                  {/* Icono tipo */}
                  <div className="text-2xl">
                    {documentTypeIcons[doc.document_type]}
                  </div>

                  {/* Contenido */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h4 className="font-medium flex items-center gap-2">
                          {doc.title}
                          {doc.is_urgent && (
                            <AlertTriangle className="w-4 h-4 text-destructive" />
                          )}
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          {documentTypeLabels[doc.document_type]}
                        </p>
                      </div>
                      <Badge variant={status.variant} className="flex items-center gap-1">
                        {status.icon}
                        {status.label}
                      </Badge>
                    </div>

                    {doc.description && (
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                        {doc.description}
                      </p>
                    )}

                    {/* Fechas */}
                    {(doc.start_date || doc.end_date) && (
                      <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                        <Calendar className="w-4 h-4" />
                        {doc.start_date && format(new Date(doc.start_date), 'dd/MM/yyyy', { locale: es })}
                        {doc.start_date && doc.end_date && ' - '}
                        {doc.end_date && format(new Date(doc.end_date), 'dd/MM/yyyy', { locale: es })}
                      </div>
                    )}

                    {/* Respuesta de admin */}
                    {doc.admin_response && (
                      <div className="mt-3 p-3 rounded-lg bg-muted/50 border-l-4 border-primary">
                        <p className="text-xs font-medium text-muted-foreground mb-1">
                          Respuesta de Administración:
                        </p>
                        <p className="text-sm">{doc.admin_response}</p>
                      </div>
                    )}

                    {/* Fecha de envío */}
                    <p className="text-xs text-muted-foreground mt-2">
                      Enviado {formatDistanceToNow(new Date(doc.created_at), {
                        addSuffix: true,
                        locale: es
                      })}
                    </p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <EmployeeDocumentForm
        open={formOpen}
        onOpenChange={setFormOpen}
        onSuccess={fetchDocuments}
      />
    </div>
  );
}
